import { apiFetch } from "@/lib/api/fetcher";

const PRE_LOGIN_PATH = "/v1/ott/subscription-plan/pre-login";
const CHECKOUT_PATH = "/study-v2/pi/subscription";

type OttPricingTier = {
  _id: string;
  name: string;
  duration: number;
  price: number;
  discountAmount?: number;
  tag?: string;
  status: string;
};

type OttSubscriptionPlan = {
  _id: string;
  pricingTiers?: OttPricingTier[];
};

type PreLoginResponse = {
  success: boolean;
  data?: OttSubscriptionPlan[];
};

export type PricingPlan = {
  /** The pricing tier's own id — reported as `tier_id` in the purchase analytics payload. */
  id: string;
  name: string;
  /** Parent subscription plan id and raw tier amounts, also reported as-is to analytics. */
  planId: string;
  amount: number;
  durationDays: number;
  billingCycle: string;
  originalPrice?: string;
  price: string;
  priceSuffix: string;
  savingsNote?: string;
  badge?: string;
  highlighted: boolean;
  features: string[];
  ctaLabel: string;
  checkoutUrl: string;
};

/** Builds a Pi subscription checkout URL; falls back to the plain checkout page when ids are missing. */
export function buildCheckoutUrl(planId?: string, tierId?: string) {
  const baseUrl = (process.env.NEXT_PUBLIC_PW_URL ?? "").replace(/\/$/, "");
  const checkoutUrl = `${baseUrl}${CHECKOUT_PATH}`;
  const params = new URLSearchParams();

  if (planId) params.set("planId", planId);
  if (tierId) params.set("tierId", tierId);

  const query = params.toString();
  return query ? `${checkoutUrl}?${query}` : checkoutUrl;
}

function getPeriodSuffix(duration: number) {
  if (duration === 30) return "/month";
  if (duration === 360 || duration === 365) return "/year";
  if (duration % 30 === 0) return `/${duration / 30} months`;

  return `/${duration} days`;
}

/** Renders amounts the way the app does elsewhere: `₹2,399`. */
function formatAmount(amount: number) {
  return `₹${amount.toLocaleString("en-IN")}`;
}

function toPricingPlan(planId: string, tier: OttPricingTier): PricingPlan {
  const finalPrice = tier.discountAmount ?? tier.price;
  const hasDiscount = tier.price > finalPrice;
  const suffix = getPeriodSuffix(tier.duration);

  return {
    id: tier._id,
    name: tier.name,
    planId,
    amount: finalPrice,
    durationDays: tier.duration,
    billingCycle: `Renews every ${suffix.slice(1)}`,
    originalPrice: hasDiscount ? formatAmount(tier.price) : undefined,
    price: formatAmount(finalPrice),
    priceSuffix: suffix,
    savingsNote: hasDiscount
      ? `Save ${formatAmount(tier.price - finalPrice)}`
      : undefined,
    badge: tier.tag,
    highlighted: Boolean(tier.tag),
    features: ["Cancel anytime", "All Pi content unlocked", "Works on web + app"],
    ctaLabel: `Choose ${tier.name}`,
    checkoutUrl: buildCheckoutUrl(planId, tier._id),
  };
}

async function loadPricingPlans(): Promise<PricingPlan[]> {
  const organizationId = process.env.NEXT_PUBLIC_PW_ORG_ID;

  if (!organizationId) {
    return [];
  }

  const payload = await apiFetch<PreLoginResponse>(
    `${PRE_LOGIN_PATH}?organizationId=${organizationId}`,
  );

  const plan = payload.success ? payload.data?.[0] : undefined;

  if (!plan) {
    return [];
  }

  const activePlans = (plan.pricingTiers ?? [])
    .filter((tier) => tier.status === "Active")
    .sort((a, b) => a.duration - b.duration)
    .map((tier) => toPricingPlan(plan._id, tier));

  const highlightedPlanIndex = activePlans.findIndex((tier) => tier.highlighted);

  return activePlans.map((tier, index) => ({
    ...tier,
    highlighted: index === highlightedPlanIndex,
  }));
}

let cachedPlansPromise: Promise<PricingPlan[]> | undefined;

/** Cached so the pricing section and the CTA checkout links share one request. */
export function fetchPricingPlans(): Promise<PricingPlan[]> {
  cachedPlansPromise ??= loadPricingPlans().catch(() => []);

  return cachedPlansPromise.then((plans) => {
    // Don't cache a failed/empty fetch — let the next caller retry.
    if (!plans.length) {
      cachedPlansPromise = undefined;
    }
    return plans;
  });
}

/** Checkout link for the base (shortest-duration) tier, used by CTAs that don't name a plan. */
export async function fetchDefaultCheckoutUrl() {
  const [basePlan] = await fetchPricingPlans();

  return basePlan?.checkoutUrl ?? buildCheckoutUrl();
}
